import { SxProps, Theme, useTheme } from '@mui/material/styles';
import MuiTextField, {
  BaseTextFieldProps as MuiBaseTextFieldProps,
  TextFieldProps as MuiTextFieldProps
} from '@mui/material/TextField';
import React from 'react';

import { toSxArray } from '_lib/utils/helpers';

import { onEnterWrapper } from '../../utils/keyboardEventUtils';
import { FieldIssueProps, useErrorProps } from '../../utils/validationErrorUtils';

import type { Merge } from 'type-fest';

const invertedColorStyles = (theme: Theme): SxProps<Theme> => ({
  '& label': {
    color: theme.palette.primaryInverted.main
  },
  '& label.Mui-focused': {
    color: theme.palette.primaryInverted.main
  },
  '& input': {
    color: theme.palette.primaryInverted.main
  },
  '& .MuiInputAdornment-root': {
    color: theme.palette.primaryInverted.main,
    '& .MuiButtonBase-root': {
      color: theme.palette.primaryInverted.main
    }
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.primaryInverted.main
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primaryInverted.main
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primaryInverted.main
    }
  }
});

export type TextFieldProps = Merge<
  Omit<MuiTextFieldProps, 'error'>,
  {
    /**
     * A boolean to signify whether or not there is a validation error
     * OR
     * An FieldIssueProps object to be used to determine if there is a validation error
     * @default false
     */
    error?: MuiTextFieldProps['error'] | FieldIssueProps;
    /**
     * Set this to true to invert the TextFields colors
     * @default false
     */
    invertColors?: boolean;
    /**
     * @default 'dense'
     */
    margin?: MuiBaseTextFieldProps['margin'];
    /** handler for user pressing enter key */
    onEnter?: MuiBaseTextFieldProps['onKeyDown'];
    /**
     * @default 'small'
     */
    size?: MuiBaseTextFieldProps['size'];
    /** @default '' */
    value?: MuiBaseTextFieldProps['value'];
  }
>;

type TextFieldHookProps = TextFieldProps;

export const useTextFieldProps = <T extends TextFieldHookProps>(props: T) => {
  const { error, helperText, invertColors, margin, onEnter, onKeyDown, size, sx, ...restOfProps } = props;
  const errorProps = useErrorProps({
    helperText,
    error: typeof error === 'boolean' ? error : false,
    fieldIssueProps: error && typeof error !== 'boolean' ? error : undefined
  });
  const theme = useTheme();
  return {
    error: errorProps.error,
    helperText: errorProps.helperText,
    margin: margin || 'dense',
    onKeyDown: onKeyDown || (onEnter && onEnterWrapper(onEnter)),
    size: size || 'small',
    sx: invertColors ? [...toSxArray(sx), invertedColorStyles(theme)] : toSxArray(sx),
    ...restOfProps
  };
};

export const TextField = (props: TextFieldProps) => {
  const { value, ...restOfProps } = props;
  const textFieldProps = useTextFieldProps(restOfProps);
  return <MuiTextField {...textFieldProps} value={value || ''} />;
};
